import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../../../../shared/i18n/i18n";
import { formatFullDateForUiLanguage } from "../../../../shared/i18n/dateFormatting";
import { TranslationKey } from "../../../../shared/i18n/i18n";
import { FaBan, FaCheck, FaEnvelope, FaTrash, FaUserEdit, FaUserMinus } from "react-icons/fa";
import { MdAdd, MdArchive, MdCalendarMonth, MdUnarchive } from "react-icons/md";
import { PiSealCheck } from "react-icons/pi";
import {
  archiveAdminConnectedDomain,
  archiveAdminOrganization,
  archiveAdminManagedDomain,
  checkAdminManagedDomainAvailability,
  cancelAdminOrganizationInvitation,
  createAdminConnectedDomain,
  createAdminOrganization,
  createAdminManagedDomain,
  deleteAdminConnectedDomain,
  deleteAdminManagedDomain,
  extractRequestErrorCode,
  getAdminOrganizationDomainSettings,
  getAdminOrganization,
  getAdminOrganizationInternals,
  getAdminProfile,
  inviteAdminOrganizationUser,
  listAdminConnectedDomains,
  listAdminOrganizationUsers,
  listAdminManagedDomains,
  removeAdminOrganizationUser,
  resendAdminOrganizationInvitation,
  unarchiveAdminConnectedDomain,
  unarchiveAdminManagedDomain,
  unarchiveAdminOrganization,
  updateAdminOrganizationDomainSettings,
  updateAdminOrganizationInternals,
  updateAdminOrganization,
  updateAdminOrganizationSubscription,
  updateAdminOrganizationUserRole,
  markAdminConnectedDomainVerified,
  verifyAdminConnectedDomain,
} from "../../../../shared/requests";
import {
  AdminOrganizationArchiveRequest,
  AdminOrganizationDomainSettingsDetail,
  AdminOrganizationDomainSettingsGetRequest,
  AdminOrganizationDomainSettingsUpdateRequest,
  AdminOrganizationCancelInvitationRequest,
  AdminOrganizationCreateRequest,
  AdminOrganizationDetail,
  AdminOrganizationDraft,
  AdminOrganizationGetRequest,
  AdminOrganizationInternalsDetail,
  AdminOrganizationInternalsDraft,
  AdminOrganizationInternalsGetRequest,
  AdminOrganizationInviteUserRequest,
  AdminOrganizationPermissions,
  AdminOrganizationRemoveUserRequest,
  AdminOrganizationResendInvitationRequest,
  AdminOrganizationSubscriptionDetail,
  AdminOrganizationSubscriptionUpdateRequest,
  AdminOrganizationInternalsUpdateRequest,
  AdminOrganizationUpdateRequest,
  AdminOrganizationUpdateUserRoleRequest,
  AdminOrganizationUserItem,
  AdminOrganizationUserRole,
  AdminConnectedDomainCreateRequest,
  AdminConnectedDomainDetail,
  AdminConnectedDomainsListRequest,
  AdminConnectedDomainVerificationDetail,
  AdminManagedDomainCreateRequest,
  AdminManagedDomainAvailabilityDetail,
  AdminManagedDomainDetail,
  AdminManagedDomainsListRequest,
} from "../../../types/adminEntities";
import { useAdminOrganizationContextCache } from "../AdminOrganizationContextCache";
import { useAdminLoading } from "../AdminLoadingContext";
import { AdminNavSelection } from "../AdminNavList";
import { useAdminOrganizationContext } from "../useAdminOrganizationContext";
import { AdminActionButton } from "../../common/AdminActionButton";
import { AppPopup } from "../../../../shared/components/AppPopup";
import { AdminDetailActions } from "./AdminDetailActions";
import { AdminQuotaLimitField } from "./AdminQuotaLimitField";
import { AdminDetailSectionLayout } from "./AdminDetailSectionLayout";
import { AdminSelectField } from "./AdminSelectField";
import { AdminTextField } from "./AdminTextField";
import { DisabledActionReason, hasOrganizationPermission } from "../../../utils/adminPermissions";
import { MANAGED_DOMAIN_SUFFIX, TRIAL_HOSTNAME } from "../../../../shared/config/runtimeConfig";

type OrganizationDetailSectionProps = {
  currentUserSuperAdmin: boolean;
  organizationId: null | string;
  organizationTab: OrganizationDetailTab;
  isActive: boolean;
  isDesktopNavHidden: boolean;
  isMobileViewport: boolean;
  onCloseDesktopNav: () => void;
  onEntityMutated: () => void;
  onOpenDesktopNav: () => void;
  onSelectionChange: (selection: AdminNavSelection | null) => void;
  t: (key: TranslationKey) => string;
};

type OrganizationDetailMode = "create" | "edit" | "view";
type OrganizationDetailTab = "details" | "domains" | "internals" | "subscription" | "users";
type OrganizationPopup =
  | "add-user"
  | "add-connected-domain"
  | "add-managed-domain"
  | "archive"
  | "archive-connected-domain"
  | "archive-managed-domain"
  | "cancel-invitation"
  | "change-role"
  | "delete-connected-domain"
  | "delete-managed-domain"
  | "disable-trial-domain"
  | "date-picker"
  | "discard"
  | "enable-trial-domain"
  | "remove-user"
  | "resend-invitation"
  | "unarchive"
  | "unarchive-connected-domain"
  | "unarchive-managed-domain"
  | "verify-connected-domain";

type AdminOrganizationSubscriptionDraft = {
  expiresAt: null | string;
  maxActiveSigns: string;
  maxActiveSites: string;
  maxActiveTranslations: string;
  maxActiveUsers: string;
  maxTranslationLength: string;
};

type ManagedDomainDraft = {
  subdomainLabel: string;
};

type ConnectedDomainDraft = {
  hostname: string;
  managedDomainId: string;
};

const DEFAULT_ORGANIZATION_DRAFT: AdminOrganizationDraft = {
  description: "",
  name: "",
};
const DEFAULT_ORGANIZATION_INTERNALS_DRAFT: AdminOrganizationInternalsDraft = {
  crmId: "",
};
const DEFAULT_SUBSCRIPTION_DRAFT: AdminOrganizationSubscriptionDraft = {
  expiresAt: null,
  maxActiveSigns: "",
  maxActiveSites: "",
  maxActiveTranslations: "",
  maxActiveUsers: "",
  maxTranslationLength: "",
};
const DEFAULT_MANAGED_DOMAIN_DRAFT: ManagedDomainDraft = {
  subdomainLabel: "",
};
const DEFAULT_CONNECTED_DOMAIN_DRAFT: ConnectedDomainDraft = {
  hostname: "",
  managedDomainId: "",
};
const ORGANIZATION_USERS_LIMIT = 25;
const ORGANIZATION_ROLES: AdminOrganizationUserRole[] = ["OWNER", "ADMINISTRATOR", "EDITOR", "VIEWER"];
const DEFAULT_INVITE_DRAFT = {
  email: "",
  role: "VIEWER" as AdminOrganizationUserRole,
};

const resolveAllowedOrganizationRoles = (canManageOrganizationOwners: boolean): AdminOrganizationUserRole[] => (
  canManageOrganizationOwners ? ORGANIZATION_ROLES : ORGANIZATION_ROLES.filter((role) => role !== "OWNER")
);

export const OrganizationDetailSection = ({
  currentUserSuperAdmin,
  organizationId,
  organizationTab,
  isActive,
  isDesktopNavHidden,
  isMobileViewport,
  onCloseDesktopNav,
  onEntityMutated,
  onOpenDesktopNav,
  onSelectionChange,
  t,
}: OrganizationDetailSectionProps) => {
  const { language } = useI18n();
  const { track } = useAdminLoading();
  const { ensureOrganizationContext } = useAdminOrganizationContextCache();
  const { organizationContext, isLoading: isOrganizationContextLoading } = useAdminOrganizationContext(organizationId);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const usersRequestIdRef = useRef(0);
  const [activeTab, setActiveTab] = useState<OrganizationDetailTab>("details");
  const [organizationPermissions, setOrganizationPermissions] = useState<AdminOrganizationPermissions | null>(null);
  const [detail, setDetail] = useState<AdminOrganizationDetail | null>(null);
  const [draft, setDraft] = useState<AdminOrganizationDraft>(DEFAULT_ORGANIZATION_DRAFT);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [internalsDetail, setInternalsDetail] = useState<AdminOrganizationInternalsDetail | null>(null);
  const [internalsDraft, setInternalsDraft] = useState<AdminOrganizationInternalsDraft>(DEFAULT_ORGANIZATION_INTERNALS_DRAFT);
  const [internalsErrorMessage, setInternalsErrorMessage] = useState<null | string>(null);
  const [inviteDraft, setInviteDraft] = useState(DEFAULT_INVITE_DRAFT);
  const [currentUserId, setCurrentUserId] = useState<null | string>(null);
  const [domainSettingsDetail, setDomainSettingsDetail] = useState<AdminOrganizationDomainSettingsDetail | null>(null);
  const [managedDomainDraft, setManagedDomainDraft] = useState<ManagedDomainDraft>(DEFAULT_MANAGED_DOMAIN_DRAFT);
  const [managedDomainAvailability, setManagedDomainAvailability] = useState<AdminManagedDomainAvailabilityDetail | null>(null);
  const [isManagedDomainAvailabilityLoading, setIsManagedDomainAvailabilityLoading] = useState(false);
  const [connectedDomainDraft, setConnectedDomainDraft] = useState<ConnectedDomainDraft>(DEFAULT_CONNECTED_DOMAIN_DRAFT);
  const [managedDomains, setManagedDomains] = useState<AdminManagedDomainDetail[]>([]);
  const [selectedManagedDomainId, setSelectedManagedDomainId] = useState<null | string>(null);
  const [connectedDomains, setConnectedDomains] = useState<AdminConnectedDomainDetail[]>([]);
  const [selectedConnectedDomainId, setSelectedConnectedDomainId] = useState<null | string>(null);
  const [connectedDomainVerificationResult, setConnectedDomainVerificationResult] = useState<AdminConnectedDomainVerificationDetail | null>(null);
  const [connectedDomainVerificationOverride, setConnectedDomainVerificationOverride] = useState(false);
  const [connectedDomainVerificationErrorMessage, setConnectedDomainVerificationErrorMessage] = useState<null | string>(null);
  const [domainErrorMessage, setDomainErrorMessage] = useState<null | string>(null);
  const [isDomainsLoading, setIsDomainsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isInternalsLoading, setIsInternalsLoading] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [mode, setMode] = useState<OrganizationDetailMode>("view");
  const [isSubscriptionEditing, setIsSubscriptionEditing] = useState(false);
  const [popup, setPopup] = useState<OrganizationPopup | null>(null);
  const [pendingTab, setPendingTab] = useState<OrganizationDetailTab | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminOrganizationUserItem | null>(null);
  const [subscriptionDetail, setSubscriptionDetail] = useState<AdminOrganizationSubscriptionDetail | null>(null);
  const [subscriptionDraft, setSubscriptionDraft] = useState<AdminOrganizationSubscriptionDraft>(DEFAULT_SUBSCRIPTION_DRAFT);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] = useState<null | string>(null);
  const [subscriptionPickerValue, setSubscriptionPickerValue] = useState("");
  const [userRoleDraft, setUserRoleDraft] = useState<AdminOrganizationUserRole>("VIEWER");
  const [users, setUsers] = useState<AdminOrganizationUserItem[]>([]);
  const [usersOffset, setUsersOffset] = useState(0);
  const [usersTotalCount, setUsersTotalCount] = useState(0);

  useEffect(() => {
    if (!currentUserSuperAdmin && organizationTab === "internals" && organizationId) {
      onSelectionChange({ organizationId, organizationTab: "details", type: "organization" });
      return;
    }
    if (
      activeTab !== organizationTab
      && !(
        (mode === "edit" && activeTab === "details")
        || (mode === "edit" && activeTab === "internals")
        || (isSubscriptionEditing && activeTab === "subscription")
      )
    ) {
      setActiveTab(organizationTab);
    }
  }, [organizationTab, activeTab, currentUserSuperAdmin, isSubscriptionEditing, mode, onSelectionChange, organizationId]);

  const loadDetail = useCallback(async (nextOrganizationId: string) => {
    setErrorMessage(null);
    setIsBusy(true);
    setMode("view");
    try {
      const request: AdminOrganizationGetRequest = { organizationId: nextOrganizationId };
      const { payload } = await track(async () => getAdminOrganization(request));
      setDetail(payload);
      setDraft({
        description: payload.description ?? "",
        name: payload.name ?? "",
      });
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setDetail(null);
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  }, [t, track]);

  const loadInternals = useCallback(async (nextOrganizationId: string) => {
    if (!currentUserSuperAdmin) {
      setInternalsDetail(null);
      setInternalsDraft(DEFAULT_ORGANIZATION_INTERNALS_DRAFT);
      setInternalsErrorMessage(null);
      return;
    }
    setInternalsErrorMessage(null);
    setIsInternalsLoading(true);
    try {
      const request: AdminOrganizationInternalsGetRequest = { organizationId: nextOrganizationId };
      const { payload } = await track(async () => getAdminOrganizationInternals(request));
      setInternalsDetail(payload);
      setInternalsDraft({ crmId: payload.crmId ?? "" });
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setInternalsDetail(null);
      setInternalsErrorMessage(message || fallbackMessage);
    } finally {
      setIsInternalsLoading(false);
    }
  }, [currentUserSuperAdmin, t, track]);

  const loadUsers = useCallback(async (nextOrganizationId: string, nextOffset: number, replace: boolean) => {
    const currentRequestId = ++usersRequestIdRef.current;
    setIsUsersLoading(true);
    try {
      const { payload } = await track(async () => listAdminOrganizationUsers({
        organizationId: nextOrganizationId,
        limit: ORGANIZATION_USERS_LIMIT,
        offset: nextOffset,
      }));
      if (usersRequestIdRef.current !== currentRequestId) {
        return;
      }
      const pageItems = Array.isArray(payload.items) ? payload.items : [];
      setUsers((current) => (replace ? pageItems : [...current, ...pageItems]));
      setUsersOffset(nextOffset + pageItems.length);
      setUsersTotalCount(typeof payload.totalCount === "number" ? payload.totalCount : pageItems.length);
    } catch {
      if (usersRequestIdRef.current !== currentRequestId) {
        return;
      }
    } finally {
      if (usersRequestIdRef.current === currentRequestId) {
        setIsUsersLoading(false);
      }
    }
  }, [track]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const { payload } = await track(async () => getAdminProfile());
      setCurrentUserId(payload.id);
    } catch {
      setCurrentUserId(null);
    }
  }, [track]);

  const loadDomains = useCallback(async (nextOrganizationId: string) => {
    setDomainErrorMessage(null);
    setIsDomainsLoading(true);
    try {
      const settingsRequest: AdminOrganizationDomainSettingsGetRequest = { organizationId: nextOrganizationId };
      const managedRequest: AdminManagedDomainsListRequest = { organizationId: nextOrganizationId, includeArchived: true };
      const connectedRequest: AdminConnectedDomainsListRequest = { organizationId: nextOrganizationId, includeArchived: true };
      const [
        settingsResponse,
        managedDomainsResponse,
        connectedDomainsResponse,
      ] = await track(async () => Promise.all([
        getAdminOrganizationDomainSettings(settingsRequest),
        listAdminManagedDomains(managedRequest),
        listAdminConnectedDomains(connectedRequest),
      ]));
      setDomainSettingsDetail(settingsResponse.payload);
      setManagedDomains(Array.isArray(managedDomainsResponse.payload) ? managedDomainsResponse.payload : []);
      setConnectedDomains(Array.isArray(connectedDomainsResponse.payload) ? connectedDomainsResponse.payload : []);
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = resolveAdminDomainErrorMessage(error, t, fallbackMessage);
      setDomainSettingsDetail(null);
      setManagedDomains([]);
      setConnectedDomains([]);
      setDomainErrorMessage(message || fallbackMessage);
    } finally {
      setIsDomainsLoading(false);
    }
  }, [t, track]);

  const resetAndLoadUsers = useCallback(() => {
    if (!organizationId) {
      setUsers([]);
      setUsersOffset(0);
      setUsersTotalCount(0);
      return;
    }
    setUsers([]);
    setUsersOffset(0);
    setUsersTotalCount(0);
    void loadUsers(organizationId, 0, true);
    if (tableWrapRef.current) {
      tableWrapRef.current.scrollTop = 0;
    }
  }, [organizationId, loadUsers]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!organizationId) {
      setDetail(null);
      setOrganizationPermissions(null);
      setDraft(DEFAULT_ORGANIZATION_DRAFT);
      setErrorMessage(null);
      setInternalsDetail(null);
      setInternalsDraft(DEFAULT_ORGANIZATION_INTERNALS_DRAFT);
      setInternalsErrorMessage(null);
      setInviteDraft(DEFAULT_INVITE_DRAFT);
      setCurrentUserId(null);
      setDomainSettingsDetail(null);
      setManagedDomainDraft(DEFAULT_MANAGED_DOMAIN_DRAFT);
      setManagedDomainAvailability(null);
      setConnectedDomainDraft(DEFAULT_CONNECTED_DOMAIN_DRAFT);
      setManagedDomains([]);
      setConnectedDomains([]);
      setSelectedConnectedDomainId(null);
      setConnectedDomainVerificationResult(null);
      setConnectedDomainVerificationOverride(false);
      setConnectedDomainVerificationErrorMessage(null);
      setDomainErrorMessage(null);
      setIsDomainsLoading(false);
      setIsInternalsLoading(false);
      setIsManagedDomainAvailabilityLoading(false);
      setIsBusy(false);
      setSubscriptionDetail(null);
      setSubscriptionDraft(DEFAULT_SUBSCRIPTION_DRAFT);
      setSubscriptionErrorMessage(null);
      setIsSubscriptionEditing(false);
      setPopup(null);
      setPendingTab(null);
      setSelectedUser(null);
      setUserRoleDraft("VIEWER");
      setUsers([]);
      setUsersOffset(0);
      setUsersTotalCount(0);
      setIsUsersLoading(false);
      setSubscriptionPickerValue("");
      setActiveTab("details");
      setMode("create");
      return;
    }
    void loadDetail(organizationId);
    void loadInternals(organizationId);
  }, [organizationId, isActive, loadDetail, loadInternals]);

  useEffect(() => {
    if (!organizationId) {
      setOrganizationPermissions(null);
      setConnectedDomainDraft(DEFAULT_CONNECTED_DOMAIN_DRAFT);
      setConnectedDomains([]);
      setConnectedDomainVerificationResult(null);
      setConnectedDomainVerificationOverride(false);
      setConnectedDomainVerificationErrorMessage(null);
      setSelectedConnectedDomainId(null);
      setDomainErrorMessage(null);
      setDomainSettingsDetail(null);
      setManagedDomainAvailability(null);
      setIsDomainsLoading(false);
      setIsManagedDomainAvailabilityLoading(false);
      setManagedDomainDraft(DEFAULT_MANAGED_DOMAIN_DRAFT);
      setManagedDomains([]);
      setSubscriptionDetail(null);
      setSubscriptionDraft(DEFAULT_SUBSCRIPTION_DRAFT);
      setSubscriptionErrorMessage(null);
      setIsSubscriptionEditing(false);
      return;
    }
    setIsSubscriptionLoading(isOrganizationContextLoading);
    setSubscriptionErrorMessage(null);
    setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
    setSubscriptionDetail(organizationContext?.activeSubscription ?? null);
    if (organizationContext?.activeSubscription && !isSubscriptionEditing) {
      setSubscriptionDraft(toSubscriptionDraft(organizationContext.activeSubscription));
    }
    if (!isOrganizationContextLoading && !organizationContext) {
      setSubscriptionDraft(DEFAULT_SUBSCRIPTION_DRAFT);
      setSubscriptionErrorMessage(t("admin.detail.error.loadFailed"));
    }
  }, [organizationContext, organizationId, isOrganizationContextLoading, isSubscriptionEditing, t]);

  const canUpdateOrganization = hasOrganizationPermission(organizationPermissions, "ORGANIZATION_UPDATE");
  const canUpdateSubscription = hasOrganizationPermission(organizationPermissions, "ORGANIZATION_ACTIVE_SUBSCRIPTION_DIRECT_UPDATE");
  const canArchiveOrganization = hasOrganizationPermission(organizationPermissions, "ORGANIZATION_ARCHIVE");
  const canRetrieveOrganizationUsers = hasOrganizationPermission(organizationPermissions, "ORGANIZATION_RETRIEVE_USERS");
  const canManageOrganizationUsers = hasOrganizationPermission(organizationPermissions, "ORGANIZATION_MANAGE_USERS");
  const canManageOrganizationOwners = hasOrganizationPermission(organizationPermissions, "ORGANIZATION_MANAGE_OWNERS");
  const canManageDomains = currentUserSuperAdmin;
  const userQuotaReached = Boolean(organizationContext) && organizationContext.organizationUsage.activeUserCount >= organizationContext.activeSubscription.maxActiveUsers;
  const addUserDisabledReason: DisabledActionReason = !canManageOrganizationUsers
    ? "permission"
    : userQuotaReached
      ? "subscription-limit"
      : null;
  const updateOrganizationDisabledReason: DisabledActionReason = !canUpdateOrganization ? "permission" : null;
  const archiveOrganizationDisabledReason: DisabledActionReason = !canArchiveOrganization ? "permission" : null;
  const updateSubscriptionDisabledReason: DisabledActionReason = !canUpdateSubscription ? "permission" : null;

  useEffect(() => {
    if (organizationPermissions && !canRetrieveOrganizationUsers && activeTab === "users") {
      setActiveTab("details");
    }
  }, [organizationPermissions, activeTab, canRetrieveOrganizationUsers]);

  useEffect(() => {
    if (!canManageDomains && activeTab === "domains") {
      setActiveTab("details");
    }
  }, [activeTab, canManageDomains]);

  useEffect(() => {
    if (!isActive || activeTab !== "users" || !organizationId) {
      return;
    }
    if (!canRetrieveOrganizationUsers) {
      return;
    }
    resetAndLoadUsers();
    if (!currentUserId) {
      void loadCurrentUser();
    }
  }, [organizationId, activeTab, canRetrieveOrganizationUsers, currentUserId, isActive, loadCurrentUser, resetAndLoadUsers]);

  useEffect(() => {
    if (!isActive || activeTab !== "domains" || !organizationId) {
      return;
    }
    void loadCurrentUser();
    void loadDomains(organizationId);
  }, [organizationId, activeTab, isActive, loadCurrentUser, loadDomains]);

  const canLoadMoreUsers = users.length < usersTotalCount;

  useEffect(() => {
    const root = tableWrapRef.current;
    const target = loadMoreRef.current;
    if (!root || !target || isUsersLoading || !canLoadMoreUsers || activeTab !== "users" || !organizationId) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          void loadUsers(organizationId, usersOffset, false);
        }
      },
      { root, rootMargin: "0px 0px 120px 0px", threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [organizationId, activeTab, canLoadMoreUsers, isUsersLoading, loadUsers, usersOffset]);

  const handleCreate = () => {
    onSelectionChange({ create: true, type: "organization" });
  };

  const handleEdit = () => {
    if (!detail || !canUpdateOrganization) {
      return;
    }
    setDraft({
      description: detail.description,
      name: detail.name,
    });
    setMode("edit");
  };

  const handleEditSubscription = () => {
    if (!subscriptionDetail || !canUpdateSubscription) {
      return;
    }
    setSubscriptionDraft(toSubscriptionDraft(subscriptionDetail));
    setSubscriptionErrorMessage(null);
    setIsSubscriptionEditing(true);
  };

  const handleEditInternals = () => {
    if (!internalsDetail || !currentUserSuperAdmin) {
      return;
    }
    setInternalsDraft({ crmId: internalsDetail.crmId ?? "" });
    setInternalsErrorMessage(null);
    setMode("edit");
  };

  const navigateToOrganizationTab = (nextTab: OrganizationDetailTab) => {
    if (!organizationId) {
      setActiveTab(nextTab);
      return;
    }
    onSelectionChange({ organizationId, organizationTab: nextTab, type: "organization" });
  };

  const handleTabChange = (nextTab: OrganizationDetailTab) => {
    if (nextTab === activeTab) {
      return;
    }
    if (
      (mode === "edit" && activeTab === "details")
      || (mode === "edit" && activeTab === "internals")
      || (isSubscriptionEditing && activeTab === "subscription")
    ) {
      setPendingTab(nextTab);
      setPopup("discard");
      return;
    }
    navigateToOrganizationTab(nextTab);
  };

  const confirmDiscard = () => {
    if (!organizationId) {
      onSelectionChange(null);
      return;
    }
    if (activeTab === "details") {
      setMode("view");
      void loadDetail(organizationId);
    } else if (activeTab === "internals") {
      setMode("view");
      void loadInternals(organizationId);
    } else if (activeTab === "subscription") {
      setIsSubscriptionEditing(false);
      if (subscriptionDetail) {
        setSubscriptionDraft(toSubscriptionDraft(subscriptionDetail));
      }
    }
    if (pendingTab) {
      navigateToOrganizationTab(pendingTab);
    }
    setPendingTab(null);
  };

  const handleSave = async () => {
    setErrorMessage(null);
    setInternalsErrorMessage(null);
    setIsBusy(true);
    try {
      if (mode === "create") {
        const request: AdminOrganizationCreateRequest = {
          description: draft.description,
          name: draft.name,
        };
        const { payload } = await track(async () => createAdminOrganization(request));
        const createdId = payload.id;
        onEntityMutated();
        if (createdId) {
          onSelectionChange({ organizationId: createdId, type: "organization" });
        }
      } else if (mode === "edit" && activeTab === "internals" && organizationId) {
        const normalizedCrmId = internalsDraft.crmId.trim();
        const request: AdminOrganizationInternalsUpdateRequest = {
          organizationId,
          crmId: normalizedCrmId.length > 0 ? normalizedCrmId : null,
        };
        await track(async () => updateAdminOrganizationInternals(request));
        setInternalsDetail({
          organizationId,
          crmId: request.crmId,
        });
        setInternalsDraft({ crmId: normalizedCrmId });
        setMode("view");
        onEntityMutated();
      } else if (mode === "edit" && detail) {
        const request: AdminOrganizationUpdateRequest = {
          organizationId: detail.id,
          description: draft.description,
          name: draft.name,
        };
        await track(async () => updateAdminOrganization(request));
        setDetail({ ...detail, description: draft.description, name: draft.name });
        setMode("view");
        onEntityMutated();
      }
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      if (activeTab === "internals") {
        setInternalsErrorMessage(message || fallbackMessage);
      } else {
        setErrorMessage(message || fallbackMessage);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveSubscription = async () => {
    if (!organizationId) {
      return;
    }
    setSubscriptionErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminOrganizationSubscriptionUpdateRequest = {
        organizationId,
        expiresAt: subscriptionDraft.expiresAt,
        maxActiveSigns: parseSubscriptionNumber(subscriptionDraft.maxActiveSigns, t("admin.detail.field.maxActiveSigns")),
        maxActiveSites: parseSubscriptionNumber(subscriptionDraft.maxActiveSites, t("admin.detail.field.maxActiveSites")),
        maxActiveTranslations: parseSubscriptionNumber(subscriptionDraft.maxActiveTranslations, t("admin.detail.field.maxActiveTranslations")),
        maxActiveUsers: parseSubscriptionNumber(subscriptionDraft.maxActiveUsers, t("admin.detail.field.maxActiveUsers")),
        maxTranslationLength: parseSubscriptionNumber(subscriptionDraft.maxTranslationLength, t("admin.detail.field.maxTranslationLength")),
      };
      await track(async () => updateAdminOrganizationSubscription(request));
      const refreshedContext = await ensureOrganizationContext(organizationId, { force: true });
      setSubscriptionDetail(refreshedContext?.activeSubscription ?? null);
      if (refreshedContext?.activeSubscription) {
        setSubscriptionDraft(toSubscriptionDraft(refreshedContext.activeSubscription));
      }
      setIsSubscriptionEditing(false);
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSubscriptionErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const updateDomainSettings = async (overrides: Partial<AdminOrganizationDomainSettingsDetail>) => {
    if (!organizationId || !domainSettingsDetail || !canManageDomains) {
      return;
    }
    setDomainErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminOrganizationDomainSettingsUpdateRequest = {
        organizationId,
        trialDomainEnabled: overrides.trialDomainEnabled ?? domainSettingsDetail.trialDomainEnabled,
      };
      await track(async () => updateAdminOrganizationDomainSettings(request));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = resolveAdminDomainErrorMessage(error, t, fallbackMessage);
      setDomainErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmArchive = async () => {
    if (!detail) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminOrganizationArchiveRequest = { organizationId: detail.id };
      await track(async () => archiveAdminOrganization(request));
      if (organizationId) {
        await loadDetail(organizationId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.archiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmUnarchive = async () => {
    if (!detail) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminOrganizationArchiveRequest = { organizationId: detail.id };
      await track(async () => unarchiveAdminOrganization(request));
      if (organizationId) {
        await loadDetail(organizationId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.unarchiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const handleAddUser = () => {
    if (addUserDisabledReason !== null) {
      return;
    }
    setInviteDraft(DEFAULT_INVITE_DRAFT);
    setPopup("add-user");
  };

  const handleAddManagedDomain = () => {
    setManagedDomainDraft(DEFAULT_MANAGED_DOMAIN_DRAFT);
    setManagedDomainAvailability(null);
    setPopup("add-managed-domain");
  };

  const checkManagedDomainAvailability = useCallback(async (subdomainLabel: string) => {
    if (!organizationId) {
      return null;
    }
    setIsManagedDomainAvailabilityLoading(true);
    try {
      const { payload } = await track(async () => checkAdminManagedDomainAvailability({
        organizationId,
        subdomainLabel,
      }));
      setManagedDomainAvailability(payload);
      return payload;
    } catch {
      setDomainErrorMessage(t("admin.detail.error.saveFailed"));
      setManagedDomainAvailability(null);
      return null;
    } finally {
      setIsManagedDomainAvailabilityLoading(false);
    }
  }, [organizationId, track]);

  const confirmAddManagedDomain = async () => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    const availability = await checkManagedDomainAvailability(managedDomainDraft.subdomainLabel);
    if (!availability?.available || !availability.normalizedSubdomainLabel) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminManagedDomainCreateRequest = {
        organizationId,
        subdomainLabel: availability.normalizedSubdomainLabel,
      };
      await track(async () => createAdminManagedDomain(request));
      setPopup(null);
      setManagedDomainAvailability(null);
      setManagedDomainDraft(DEFAULT_MANAGED_DOMAIN_DRAFT);
      await loadDomains(organizationId);
      onEntityMutated();
    } catch {
      setDomainErrorMessage(t("admin.detail.error.saveFailed"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleArchiveManagedDomain = async (managedDomainId: string) => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      await track(async () => archiveAdminManagedDomain({ organizationId, managedDomainId }));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch {
      setDomainErrorMessage(t("admin.detail.error.archiveFailed"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleUnarchiveManagedDomain = async (managedDomainId: string) => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      await track(async () => unarchiveAdminManagedDomain({ organizationId, managedDomainId }));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      setDomainErrorMessage(resolveAdminDomainErrorMessage(error, t, t("admin.detail.error.unarchiveFailed")));
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteManagedDomain = async (managedDomainId: string) => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      await track(async () => deleteAdminManagedDomain({ organizationId, managedDomainId }));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      setDomainErrorMessage(resolveAdminDomainErrorMessage(error, t, t("admin.detail.error.archiveFailed")));
    } finally {
      setIsBusy(false);
    }
  };

  const handleAddConnectedDomain = () => {
    const firstManagedDomainId = managedDomains.find((domain) => !domain.archivedAt)?.id ?? "";
    setConnectedDomainDraft({ ...DEFAULT_CONNECTED_DOMAIN_DRAFT, managedDomainId: firstManagedDomainId });
    setPopup("add-connected-domain");
  };

  const confirmAddConnectedDomain = async () => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminConnectedDomainCreateRequest = {
        organizationId,
        hostname: connectedDomainDraft.hostname,
        managedDomainId: connectedDomainDraft.managedDomainId,
      };
      await track(async () => createAdminConnectedDomain(request));
      setPopup(null);
      setConnectedDomainDraft(DEFAULT_CONNECTED_DOMAIN_DRAFT);
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      setDomainErrorMessage(resolveAdminDomainErrorMessage(error, t, t("admin.detail.error.saveFailed")));
    } finally {
      setIsBusy(false);
    }
  };

  const handleArchiveConnectedDomain = async (connectedDomainId: string) => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      await track(async () => archiveAdminConnectedDomain({ organizationId, connectedDomainId }));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch {
      setDomainErrorMessage(t("admin.detail.error.archiveFailed"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleUnarchiveConnectedDomain = async (connectedDomainId: string) => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      await track(async () => unarchiveAdminConnectedDomain({ organizationId, connectedDomainId }));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      setDomainErrorMessage(resolveAdminDomainErrorMessage(error, t, t("admin.detail.error.unarchiveFailed")));
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteConnectedDomain = async (connectedDomainId: string) => {
    if (!organizationId || !canManageDomains) {
      return;
    }
    setIsBusy(true);
    try {
      await track(async () => deleteAdminConnectedDomain({ organizationId, connectedDomainId }));
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      setDomainErrorMessage(resolveAdminDomainErrorMessage(error, t, t("admin.detail.error.archiveFailed")));
    } finally {
      setIsBusy(false);
    }
  };

  const handleOpenVerifyConnectedDomain = (connectedDomainId: string) => {
    setSelectedConnectedDomainId(connectedDomainId);
    setConnectedDomainVerificationResult(null);
    setConnectedDomainVerificationOverride(false);
    setConnectedDomainVerificationErrorMessage(null);
    setPopup("verify-connected-domain");
  };

  const handleVerifyConnectedDomain = async () => {
    if (!organizationId || !canManageDomains || !selectedConnectedDomainId) {
      return;
    }
    setConnectedDomainVerificationErrorMessage(null);
    setIsBusy(true);
    try {
      const { payload } = await track(async () => verifyAdminConnectedDomain({
        organizationId,
        connectedDomainId: selectedConnectedDomainId,
      }));
      setConnectedDomainVerificationResult(payload);
      await loadDomains(organizationId);
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      setConnectedDomainVerificationErrorMessage(resolveAdminDomainErrorMessage(error, t, fallbackMessage));
    } finally {
      setIsBusy(false);
    }
  };

  const confirmSaveConnectedDomainVerification = async () => {
    if (!organizationId || !canManageDomains || !selectedConnectedDomainId) {
      return;
    }
    if (!connectedDomainVerificationResult?.verified && !connectedDomainVerificationOverride) {
      return;
    }
    setConnectedDomainVerificationErrorMessage(null);
    setIsBusy(true);
    try {
      await track(async () => markAdminConnectedDomainVerified({
        organizationId,
        connectedDomainId: selectedConnectedDomainId,
      }));
      await loadDomains(organizationId);
      onEntityMutated();
      setPopup(null);
      setSelectedConnectedDomainId(null);
      setConnectedDomainVerificationResult(null);
      setConnectedDomainVerificationOverride(false);
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      setConnectedDomainVerificationErrorMessage(resolveAdminDomainErrorMessage(error, t, fallbackMessage));
    } finally {
      setIsBusy(false);
    }
  };

  const closeConnectedDomainVerificationPopup = () => {
    setPopup(null);
    setSelectedConnectedDomainId(null);
    setConnectedDomainVerificationResult(null);
    setConnectedDomainVerificationOverride(false);
    setConnectedDomainVerificationErrorMessage(null);
  };

  const confirmAddUser = async () => {
    if (!organizationId || !canManageOrganizationUsers || userQuotaReached || (inviteDraft.role === "OWNER" && !canManageOrganizationOwners)) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminOrganizationInviteUserRequest = {
        organizationId,
        email: inviteDraft.email,
        role: inviteDraft.role,
      };
      await track(async () => inviteAdminOrganizationUser(request));
      await ensureOrganizationContext(organizationId, { force: true });
      setPopup(null);
      setInviteDraft(DEFAULT_INVITE_DRAFT);
      resetAndLoadUsers();
    } catch {
    } finally {
      setIsBusy(false);
    }
  };

  const handleChangeRole = (user: AdminOrganizationUserItem) => {
    if (!canManageOrganizationUsers || !user.userId || user.status !== "ACTIVE" || user.userId === currentUserId) {
      return;
    }
    if (user.role === "OWNER" && !canManageOrganizationOwners) {
      return;
    }
    setSelectedUser(user);
    setUserRoleDraft(user.role);
    setPopup("change-role");
  };

  const confirmChangeRole = async () => {
    if (!organizationId || !selectedUser?.userId || !canManageOrganizationUsers || (userRoleDraft === "OWNER" && !canManageOrganizationOwners)) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminOrganizationUpdateUserRoleRequest = {
        organizationId,
        role: userRoleDraft,
        userId: selectedUser.userId,
      };
      await track(async () => updateAdminOrganizationUserRole(request));
      setPopup(null);
      setSelectedUser(null);
      resetAndLoadUsers();
    } catch {
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemoveUser = (user: AdminOrganizationUserItem) => {
    if (!canManageOrganizationUsers || !user.userId || user.status !== "ACTIVE" || user.userId === currentUserId) {
      return;
    }
    if (user.role === "OWNER" && !canManageOrganizationOwners) {
      return;
    }
    setSelectedUser(user);
    setPopup("remove-user");
  };

  const confirmRemoveUser = async () => {
    if (!organizationId || !selectedUser?.userId || !canManageOrganizationUsers || (selectedUser.role === "OWNER" && !canManageOrganizationOwners)) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminOrganizationRemoveUserRequest = {
        organizationId,
        userId: selectedUser.userId,
      };
      await track(async () => removeAdminOrganizationUser(request));
      await ensureOrganizationContext(organizationId, { force: true });
      setPopup(null);
      setSelectedUser(null);
      resetAndLoadUsers();
    } catch {
    } finally {
      setIsBusy(false);
    }
  };

  const handleCancelInvitation = (user: AdminOrganizationUserItem) => {
    if (!canManageOrganizationUsers || !user.invitationId || user.status !== "INVITED") {
      return;
    }
    if (user.role === "OWNER" && !canManageOrganizationOwners) {
      return;
    }
    setSelectedUser(user);
    setPopup("cancel-invitation");
  };

  const confirmCancelInvitation = async () => {
    if (!organizationId || !selectedUser?.invitationId || !canManageOrganizationUsers || (selectedUser.role === "OWNER" && !canManageOrganizationOwners)) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminOrganizationCancelInvitationRequest = {
        organizationId,
        invitationId: selectedUser.invitationId,
      };
      await track(async () => cancelAdminOrganizationInvitation(request));
      await ensureOrganizationContext(organizationId, { force: true });
      setPopup(null);
      setSelectedUser(null);
      resetAndLoadUsers();
    } catch {
    } finally {
      setIsBusy(false);
    }
  };

  const handleResendInvitation = (user: AdminOrganizationUserItem) => {
    if (!canManageOrganizationUsers || !user.invitationId || user.status !== "INVITED") {
      return;
    }
    if (user.role === "OWNER" && !canManageOrganizationOwners) {
      return;
    }
    setSelectedUser(user);
    setPopup("resend-invitation");
  };

  const confirmResendInvitation = async () => {
    if (!organizationId || !selectedUser?.invitationId || !canManageOrganizationUsers || (selectedUser.role === "OWNER" && !canManageOrganizationOwners)) {
      return;
    }
    setIsBusy(true);
    try {
      const request: AdminOrganizationResendInvitationRequest = {
        organizationId,
        invitationId: selectedUser.invitationId,
      };
      await track(async () => resendAdminOrganizationInvitation(request));
      setPopup(null);
      setSelectedUser(null);
      resetAndLoadUsers();
    } catch {
    } finally {
      setIsBusy(false);
    }
  };

  const handlePopupConfirm = async () => {
    const nextPopup = popup;
    setPopup(null);
    if (nextPopup === "discard") {
      confirmDiscard();
    } else if (nextPopup === "archive") {
      await confirmArchive();
    } else if (nextPopup === "unarchive") {
      await confirmUnarchive();
    } else if (nextPopup === "add-managed-domain") {
      await confirmAddManagedDomain();
    } else if (nextPopup === "add-connected-domain") {
      await confirmAddConnectedDomain();
    } else if (nextPopup === "enable-trial-domain") {
      await updateDomainSettings({ trialDomainEnabled: true });
    } else if (nextPopup === "disable-trial-domain") {
      await updateDomainSettings({ trialDomainEnabled: false });
    } else if (nextPopup === "archive-managed-domain" && selectedManagedDomainId) {
      await handleArchiveManagedDomain(selectedManagedDomainId);
      setSelectedManagedDomainId(null);
    } else if (nextPopup === "unarchive-managed-domain" && selectedManagedDomainId) {
      await handleUnarchiveManagedDomain(selectedManagedDomainId);
      setSelectedManagedDomainId(null);
    } else if (nextPopup === "delete-managed-domain" && selectedManagedDomainId) {
      await handleDeleteManagedDomain(selectedManagedDomainId);
      setSelectedManagedDomainId(null);
    } else if (nextPopup === "archive-connected-domain" && selectedConnectedDomainId) {
      await handleArchiveConnectedDomain(selectedConnectedDomainId);
      setSelectedConnectedDomainId(null);
    } else if (nextPopup === "unarchive-connected-domain" && selectedConnectedDomainId) {
      await handleUnarchiveConnectedDomain(selectedConnectedDomainId);
      setSelectedConnectedDomainId(null);
    } else if (nextPopup === "delete-connected-domain" && selectedConnectedDomainId) {
      await handleDeleteConnectedDomain(selectedConnectedDomainId);
      setSelectedConnectedDomainId(null);
    }
  };

  const popupTitle = popup === "discard"
    ? t("admin.detail.confirm.discardTitle")
    : popup === "archive"
      ? t("admin.detail.confirm.archiveTitle")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchiveTitle")
        : popup === "enable-trial-domain"
          ? t("admin.organization.domains.trial.enableTitle")
          : popup === "disable-trial-domain"
            ? t("admin.organization.domains.trial.disableTitle")
            : popup === "verify-connected-domain"
              ? t("admin.organization.domains.connected.verifyTitle")
              : popup === "archive-managed-domain"
              ? t("admin.organization.domains.managed.archiveTitle")
              : popup === "unarchive-managed-domain"
                ? t("admin.organization.domains.managed.unarchiveTitle")
                : popup === "delete-managed-domain"
                  ? t("admin.organization.domains.managed.deleteTitle")
                  : popup === "archive-connected-domain"
                    ? t("admin.organization.domains.connected.archiveTitle")
                    : popup === "unarchive-connected-domain"
                      ? t("admin.organization.domains.connected.unarchiveTitle")
                      : popup === "delete-connected-domain"
                        ? t("admin.organization.domains.connected.deleteTitle")
                    : "";

  const popupBody = popup === "discard"
    ? t("admin.detail.confirm.discard")
    : popup === "archive"
      ? t("admin.detail.confirm.archive")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchive")
        : popup === "enable-trial-domain"
          ? t("admin.organization.domains.trial.enableConfirm")
          : popup === "disable-trial-domain"
            ? t("admin.organization.domains.trial.disableConfirm")
            : popup === "archive-managed-domain"
              ? t("admin.organization.domains.managed.archiveConfirm")
              : popup === "unarchive-managed-domain"
                ? t("admin.organization.domains.managed.unarchiveConfirm")
                : popup === "delete-managed-domain"
                  ? t("admin.organization.domains.managed.deleteConfirm")
                  : popup === "archive-connected-domain"
                    ? t("admin.organization.domains.connected.archiveConfirm")
                    : popup === "unarchive-connected-domain"
                      ? t("admin.organization.domains.connected.unarchiveConfirm")
                      : popup === "delete-connected-domain"
                        ? t("admin.organization.domains.connected.deleteConfirm")
                  : "";

  const isReadOnly = mode === "view" || isBusy || (mode === "edit" && (
    activeTab === "internals"
      ? !currentUserSuperAdmin
      : !canUpdateOrganization
  ));
  const isSubscriptionReadOnly = !isSubscriptionEditing || isBusy || !canUpdateSubscription;
  const shouldRenderPane = mode === "create" || detail !== null || errorMessage !== null;
  const isSubscriptionExpired = subscriptionDetail?.expiresAt === null
    ? true
    : subscriptionDetail?.expiresAt
      ? new Date(subscriptionDetail.expiresAt).getTime() <= Date.now()
      : false;
  const allowedRoles = resolveAllowedOrganizationRoles(canManageOrganizationOwners);
  const roleOptions = allowedRoles.map((role) => ({
    label: t(`admin.organizationUsers.role.${role}` as TranslationKey),
    value: role,
  }));
  const selectedUserLabel = selectedUser?.email ?? "";
  const selectedUserNameEmailLabel = selectedUser?.name
    ? `${selectedUser.name} (${selectedUser.email})`
    : selectedUserLabel;
  const trialHostname = TRIAL_HOSTNAME.trim();
  const managedDomainOptions = managedDomains
    .filter((domain) => !domain.archivedAt)
    .map((domain) => ({
      label: toManagedHostname(domain.subdomainLabel),
      value: domain.id,
    }));
  const selectedConnectedManagedDomain = managedDomains.find((domain) => domain.id === connectedDomainDraft.managedDomainId && !domain.archivedAt) ?? null;
  const selectedConnectedDomain = connectedDomains.find((domain) => domain.id === selectedConnectedDomainId) ?? null;
  const selectedVerificationManagedDomain = managedDomains.find((domain) => domain.id === selectedConnectedDomain?.managedDomainId && !domain.archivedAt) ?? null;
  const connectedDomainVerificationCanSave = Boolean(connectedDomainVerificationResult?.verified || connectedDomainVerificationOverride);
  const managedDomainsCreateDisabled = !canManageDomains;
  const connectedDomainsCreateDisabled = !canManageDomains || managedDomainOptions.length === 0;
  const managedDomainAvailabilityState = managedDomainDraft.subdomainLabel.trim().length === 0
    ? null
    : isManagedDomainAvailabilityLoading
      ? "checking"
      : managedDomainAvailability?.available
        ? "available"
        : managedDomainAvailability?.errorCode
          ? "unavailable"
          : null;

  useEffect(() => {
    if (popup !== "add-managed-domain") {
      return;
    }
    if (managedDomainDraft.subdomainLabel.trim().length === 0) {
      setManagedDomainAvailability(null);
      setIsManagedDomainAvailabilityLoading(false);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      void checkManagedDomainAvailability(managedDomainDraft.subdomainLabel);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [checkManagedDomainAvailability, managedDomainDraft.subdomainLabel, popup]);

  useEffect(() => {
    if (!allowedRoles.includes(inviteDraft.role)) {
      setInviteDraft((current) => ({ ...current, role: allowedRoles[0] ?? "VIEWER" }));
    }
  }, [allowedRoles, inviteDraft.role]);

  useEffect(() => {
    if (!allowedRoles.includes(userRoleDraft)) {
      setUserRoleDraft(allowedRoles[0] ?? "VIEWER");
    }
  }, [allowedRoles, userRoleDraft]);

  if (!shouldRenderPane) {
    return null;
  }

  return (
    <>
      <AdminDetailSectionLayout
        actions={mode !== "create" && detail ? (
          <AdminDetailActions
            archiveDisabledReason={archiveOrganizationDisabledReason}
            canArchive={canArchiveOrganization}
            canCreate
            createActionLabel={t("admin.detail.action.createOrganization")}
            canUpdate={canUpdateOrganization}
            hasDetail={detail !== null}
            disableNonEditingActions={mode === "create" || mode === "edit"}
            isArchived={Boolean(detail?.archivedAt)}
            isBusy={isBusy}
            isDesktopNavHidden={isDesktopNavHidden}
            isEditing={false}
            isMobileViewport={isMobileViewport}
            onArchive={() => setPopup("archive")}
            onCloseDesktopNav={onCloseDesktopNav}
            onCreate={handleCreate}
            onDiscard={() => setPopup("discard")}
            onEdit={handleEdit}
            onOpenDesktopNav={onOpenDesktopNav}
            onSave={() => void handleSave()}
            showEditAction={false}
            onUnarchive={() => setPopup("unarchive")}
            updateDisabledReason={updateOrganizationDisabledReason}
            t={t}
          />
        ) : null}
        errorMessage={errorMessage}
        eyebrow={t("admin.detail.eyebrow")}
        title={t("admin.detail.title.organization")}
      >
        {mode === "create" ? (
          <>
            <div
              aria-label={t("admin.organization.tabs.title")}
              className="admin-profile-tabs"
              role="tablist"
            >
              <button
                aria-controls="admin-organization-details-panel"
                aria-selected="true"
                className="admin-profile-tab is-active"
                id="admin-organization-details-tab"
                role="tab"
                type="button"
              >
                {t("admin.organization.tab.details")}
              </button>
            </div>
            <div
              aria-labelledby="admin-organization-details-tab"
              className="admin-profile-panel is-active"
              id="admin-organization-details-panel"
              role="tabpanel"
            >
              <div className="admin-profile-section-header">
                <div className="admin-detail-actions admin-profile-section-actions">
                  <AdminDetailActions
                    canCreate
                    canUpdate
                    hasDetail={false}
                    isArchived={false}
                    isBusy={isBusy}
                    isDesktopNavHidden={isDesktopNavHidden}
                    isEditing
                    isMobileViewport={isMobileViewport}
                    onArchive={() => {}}
                    onCloseDesktopNav={onCloseDesktopNav}
                    onCreate={handleCreate}
                    onDiscard={() => setPopup("discard")}
                    onEdit={handleEdit}
                    onOpenDesktopNav={onOpenDesktopNav}
                    onSave={() => void handleSave()}
                    showArchiveActions={false}
                    showCreateAction={false}
                    showNavToggle={false}
                    updateDisabledReason={null}
                    onUnarchive={() => {}}
                    t={t}
                  />
                </div>
              </div>
              <form className="admin-detail-form">
                <AdminTextField
                  isReadOnly={isReadOnly}
                  label={t("admin.detail.field.name")}
                  onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, name: value }))}
                  t={t}
                  value={draft.name}
                  visibility="public"
                />
                <AdminTextField
                  isReadOnly={isReadOnly}
                  label={t("admin.detail.field.description")}
                  onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, description: value }))}
                  t={t}
                  value={draft.description}
                  visibility="internal"
                />
              </form>
            </div>
          </>
        ) : detail ? (
          <>
            <div
              aria-label={t("admin.organization.tabs.title")}
              className="admin-profile-tabs"
              role="tablist"
            >
              <button
                aria-controls="admin-organization-details-panel"
                aria-selected={activeTab === "details"}
                className={`admin-profile-tab${activeTab === "details" ? " is-active" : ""}`}
                id="admin-organization-details-tab"
                onClick={() => handleTabChange("details")}
                role="tab"
                type="button"
              >
                {t("admin.organization.tab.details")}
              </button>
              <button
                aria-controls="admin-organization-users-panel"
                aria-selected={activeTab === "users"}
                className={`admin-profile-tab${activeTab === "users" ? " is-active" : ""}`}
                disabled={!canRetrieveOrganizationUsers}
                id="admin-organization-users-tab"
                onClick={() => handleTabChange("users")}
                role="tab"
                type="button"
              >
                {t("admin.organization.tab.users")}
              </button>
              {canManageDomains ? (
                <button
                  aria-controls="admin-organization-domains-panel"
                  aria-selected={activeTab === "domains"}
                  className={`admin-profile-tab${activeTab === "domains" ? " is-active" : ""}`}
                  id="admin-organization-domains-tab"
                  onClick={() => handleTabChange("domains")}
                  role="tab"
                  type="button"
                >
                  {t("admin.organization.tab.domains")}
                </button>
              ) : null}
              <button
                aria-controls="admin-organization-subscription-panel"
                aria-selected={activeTab === "subscription"}
                className={`admin-profile-tab${activeTab === "subscription" ? " is-active" : ""}`}
                id="admin-organization-subscription-tab"
                onClick={() => handleTabChange("subscription")}
                role="tab"
                type="button"
              >
                {t("admin.organization.tab.subscription")}
              </button>
              {currentUserSuperAdmin ? (
                <button
                  aria-controls="admin-organization-internals-panel"
                  aria-selected={activeTab === "internals"}
                  className={`admin-profile-tab${activeTab === "internals" ? " is-active" : ""}`}
                  id="admin-organization-internals-tab"
                  onClick={() => handleTabChange("internals")}
                  role="tab"
                  type="button"
                >
                  {t("admin.organization.tab.internals")}
                </button>
              ) : null}
            </div>

            <div
              aria-labelledby="admin-organization-details-tab"
              className={`admin-profile-panel${activeTab === "details" ? " is-active" : ""}`}
              id="admin-organization-details-panel"
              role="tabpanel"
            >
              <div className="admin-profile-section-header">
                <div className="admin-detail-actions admin-profile-section-actions">
                  <AdminDetailActions
                    canCreate
                    canUpdate={canUpdateOrganization}
                    hasDetail={detail !== null}
                    isArchived={Boolean(detail.archivedAt)}
                    isBusy={isBusy}
                    isDesktopNavHidden={isDesktopNavHidden}
                    isEditing={mode === "edit" && activeTab === "details"}
                    isMobileViewport={isMobileViewport}
                    onArchive={() => setPopup("archive")}
                    onCloseDesktopNav={onCloseDesktopNav}
                    onCreate={handleCreate}
                    onDiscard={() => setPopup("discard")}
                    onEdit={handleEdit}
                    onOpenDesktopNav={onOpenDesktopNav}
                    onSave={() => void handleSave()}
                    showArchiveActions={false}
                    showCreateAction={false}
                    showNavToggle={false}
                    updateDisabledReason={updateOrganizationDisabledReason}
                    onUnarchive={() => {}}
                    t={t}
                  />
                </div>
              </div>
              <form className="admin-detail-form">
                <AdminTextField
                  isReadOnly={isReadOnly}
                  label={t("admin.detail.field.name")}
                  onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, name: value }))}
                  t={t}
                  value={draft.name}
                  visibility="public"
                />
                <AdminTextField
                  isReadOnly={isReadOnly}
                  label={t("admin.detail.field.description")}
                  onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, description: value }))}
                  t={t}
                  value={draft.description}
                  visibility="internal"
                />
                {detail.archivedAt ? (
                  <AdminTextField
                    isReadOnly
                    label={t("admin.detail.field.archivedAt")}
                    t={t}
                    value={formatFullDateForUiLanguage(detail.archivedAt, language)}
                    visibility="internal"
                  />
                ) : null}
              </form>
            </div>

            <div
              aria-labelledby="admin-organization-users-tab"
              className={`admin-profile-panel${activeTab === "users" ? " is-active" : ""}`}
              id="admin-organization-users-panel"
              role="tabpanel"
            >
              <div className="admin-profile-section-header">
                <div className="admin-profile-section-actions">
                  <AdminActionButton
                    ariaLabel={t("admin.organizationUsers.add")}
                    className="admin-organization-users-icon-action"
                    disabledReason={addUserDisabledReason}
                    disabled={isBusy || addUserDisabledReason !== null}
                    onClick={handleAddUser}
                    t={t}
                    title={t("admin.organizationUsers.add")}
                  >
                    <MdAdd aria-hidden="true" />
                  </AdminActionButton>
                </div>
              </div>
              <div
                className="admin-profile-terms-table-wrap admin-organization-users-table-wrap"
                ref={tableWrapRef}
              >
                <table className="admin-profile-terms-table admin-organization-users-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("admin.detail.field.name")}</th>
                      <th scope="col">{t("admin.detail.field.email")}</th>
                      <th scope="col">{t("admin.detail.field.role")}</th>
                      <th scope="col">{t("admin.organizationUsers.status")}</th>
                      <th scope="col">{t("admin.organizationUsers.invitedUntil")}</th>
                      <th scope="col">{t("admin.profile.terms.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td data-label={t("admin.detail.field.name")}>{user.name || t("admin.organizationUsers.emptyName")}</td>
                        <td data-label={t("admin.detail.field.email")}>{user.email}</td>
                        <td data-label={t("admin.detail.field.role")}>
                          <span className="admin-organization-users-role">
                            {t(`admin.organizationUsers.role.${user.role}` as TranslationKey)}
                          </span>
                        </td>
                        <td data-label={t("admin.organizationUsers.status")}>
                          {t(user.status === "ACTIVE" ? "admin.organizationUsers.status.active" : "admin.organizationUsers.status.invited")}
                        </td>
                        <td data-label={t("admin.organizationUsers.invitedUntil")}>
                          {user.expiresAt ? formatFullDateForUiLanguage(user.expiresAt, language) : t("admin.organizationUsers.notApplicable")}
                        </td>
                        <td data-label={t("admin.profile.terms.actions")}>
                          <div className="admin-organization-users-actions">
                            {(() => {
                              const disabledDueToMissingPermission = !canManageOrganizationUsers || (user.role === "OWNER" && !canManageOrganizationOwners);
                              if (user.status === "INVITED") {
                                return (
                                  <>
                                    <AdminActionButton
                                      ariaLabel={t("admin.organizationUsers.resendInvitation")}
                                      className="admin-organization-users-icon-action"
                                      disabledReason={disabledDueToMissingPermission ? "permission" : null}
                                      disabled={isBusy || disabledDueToMissingPermission}
                                      onClick={() => handleResendInvitation(user)}
                                      t={t}
                                      title={t("admin.organizationUsers.resendInvitation")}
                                    >
                                      <FaEnvelope aria-hidden="true" />
                                    </AdminActionButton>
                                    <AdminActionButton
                                      ariaLabel={t("admin.organizationUsers.cancelInvitation")}
                                      className="admin-organization-users-icon-action"
                                      disabledReason={disabledDueToMissingPermission ? "permission" : null}
                                      disabled={isBusy || disabledDueToMissingPermission}
                                      onClick={() => handleCancelInvitation(user)}
                                      t={t}
                                      title={t("admin.organizationUsers.cancelInvitation")}
                                    >
                                      <FaBan aria-hidden="true" />
                                    </AdminActionButton>
                                  </>
                                );
                              }
                              if (user.userId === currentUserId) {
                                return <span className="admin-organization-users-current">{t("admin.organizationUsers.currentUser")}</span>;
                              }
                              return (
                                <>
                                  <AdminActionButton
                                    ariaLabel={t("admin.organizationUsers.changeRole")}
                                    className="admin-organization-users-icon-action"
                                    disabledReason={disabledDueToMissingPermission ? "permission" : null}
                                    disabled={isBusy || disabledDueToMissingPermission}
                                    onClick={() => handleChangeRole(user)}
                                    t={t}
                                    title={t("admin.organizationUsers.changeRole")}
                                  >
                                    <FaUserEdit aria-hidden="true" />
                                  </AdminActionButton>
                                  <AdminActionButton
                                    ariaLabel={t("admin.organizationUsers.remove")}
                                    className="admin-organization-users-icon-action danger"
                                    disabledReason={disabledDueToMissingPermission ? "permission" : null}
                                    disabled={isBusy || disabledDueToMissingPermission}
                                    onClick={() => handleRemoveUser(user)}
                                    t={t}
                                    title={t("admin.organizationUsers.remove")}
                                  >
                                    <FaUserMinus aria-hidden="true" />
                                  </AdminActionButton>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {canLoadMoreUsers ? <div aria-hidden="true" ref={loadMoreRef} /> : null}
              </div>
              {isUsersLoading ? <p className="admin-detail-empty">{t("admin.navlist.loading")}</p> : null}
              {!isUsersLoading && users.length === 0 ? <p className="admin-detail-empty">{t("admin.organizationUsers.empty")}</p> : null}
              {!isUsersLoading && canLoadMoreUsers ? <p className="admin-detail-empty">{t("admin.navlist.scrollForMore")}</p> : null}
            </div>

            <div
              aria-labelledby="admin-organization-domains-tab"
              className={`admin-profile-panel${activeTab === "domains" ? " is-active" : ""}`}
              id="admin-organization-domains-panel"
              role="tabpanel"
            >
              {domainErrorMessage ? (
                <div className="admin-detail-state is-error">{domainErrorMessage}</div>
              ) : null}
              {isDomainsLoading ? (
                <p className="admin-detail-empty">{t("admin.detail.loading")}</p>
              ) : domainSettingsDetail ? (
                <>
                  <div className="admin-detail-group">
                    <h4 className="admin-detail-group-title">{t("admin.organization.domains.section.trial")}</h4>
                    <div className="admin-profile-terms-table-wrap admin-organization-users-table-wrap">
                      <table className="admin-profile-terms-table admin-organization-users-table">
                        <thead>
                          <tr>
                            <th scope="col">{t("admin.organization.domains.trial.hostname")}</th>
                            <th scope="col">{t("admin.organization.domains.status")}</th>
                            <th scope="col">{t("admin.profile.terms.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td data-label={t("admin.organization.domains.trial.hostname")}>{trialHostname}</td>
                            <td data-label={t("admin.organization.domains.status")}>
                              {domainSettingsDetail.trialDomainEnabled ? t("admin.organization.domains.enabled") : t("admin.organization.domains.disabled")}
                            </td>
                            <td data-label={t("admin.profile.terms.actions")}>
                              <div className="admin-organization-users-actions">
                                <AdminActionButton
                                  ariaLabel={domainSettingsDetail.trialDomainEnabled ? t("admin.organization.domains.disable") : t("admin.organization.domains.enable")}
                                  className="admin-organization-users-add"
                                  disabled={isBusy || !canManageDomains}
                                  disabledReason={!canManageDomains ? "permission" : null}
                                  onClick={() => setPopup(domainSettingsDetail.trialDomainEnabled ? "disable-trial-domain" : "enable-trial-domain")}
                                  t={t}
                                  title={domainSettingsDetail.trialDomainEnabled ? t("admin.organization.domains.disable") : t("admin.organization.domains.enable")}
                                >
                                  <span>{domainSettingsDetail.trialDomainEnabled ? t("admin.organization.domains.disable") : t("admin.organization.domains.enable")}</span>
                                </AdminActionButton>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="admin-detail-group">
                    <div className="admin-profile-section-header">
                      <h4 className="admin-detail-group-title">{t("admin.organization.domains.section.managed")}</h4>
                      <div className="admin-organization-users-actions admin-profile-section-actions">
                        <AdminActionButton
                          ariaLabel={t("admin.organization.domains.managed.add")}
                          className="admin-organization-users-icon-action"
                          disabled={isBusy || managedDomainsCreateDisabled}
                          disabledReason={!canManageDomains ? "permission" : null}
                          onClick={handleAddManagedDomain}
                          t={t}
                          title={t("admin.organization.domains.managed.add")}
                        >
                          <MdAdd aria-hidden="true" />
                        </AdminActionButton>
                      </div>
                    </div>
                    <div className="admin-profile-terms-table-wrap admin-organization-users-table-wrap">
                      <table className="admin-profile-terms-table admin-organization-users-table">
                        <thead>
                          <tr>
                            <th scope="col">{t("admin.organization.domains.managed.hostname")}</th>
                            <th scope="col">{t("admin.organization.domains.managed.label")}</th>
                            <th scope="col">{t("admin.organization.domains.status")}</th>
                            <th scope="col">{t("admin.organization.domains.createdAt")}</th>
                            <th scope="col">{t("admin.profile.terms.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {managedDomains.map((domain) => (
                            <tr key={domain.id}>
                              <td data-label={t("admin.organization.domains.managed.hostname")}>{toManagedHostname(domain.subdomainLabel)}</td>
                              <td data-label={t("admin.organization.domains.managed.label")}>{domain.subdomainLabel}</td>
                              <td data-label={t("admin.organization.domains.status")}>
                                {domain.archivedAt ? t("admin.organization.domains.status.archived") : t("admin.organization.domains.status.active")}
                              </td>
                              <td data-label={t("admin.organization.domains.createdAt")}>{formatFullDateForUiLanguage(domain.createdAt, language)}</td>
                              <td data-label={t("admin.profile.terms.actions")}>
                                <div className="admin-organization-users-actions">
                                  <AdminActionButton
                                    ariaLabel={domain.archivedAt ? t("admin.detail.action.unarchive") : t("admin.detail.action.archive")}
                                    className="admin-organization-users-icon-action"
                                    disabled={isBusy || !canManageDomains}
                                    disabledReason={!canManageDomains ? "permission" : null}
                                    onClick={() => {
                                      setSelectedManagedDomainId(domain.id);
                                      setPopup(domain.archivedAt ? "unarchive-managed-domain" : "archive-managed-domain");
                                    }}
                                    t={t}
                                    title={domain.archivedAt ? t("admin.detail.action.unarchive") : t("admin.detail.action.archive")}
                                  >
                                    {domain.archivedAt ? <MdUnarchive aria-hidden="true" /> : <MdArchive aria-hidden="true" />}
                                  </AdminActionButton>
                                  <AdminActionButton
                                    ariaLabel={t("admin.detail.action.delete")}
                                    className="admin-organization-users-icon-action danger"
                                    disabled={isBusy || !canManageDomains || !Boolean(domain.archivedAt)}
                                    disabledReason={!canManageDomains ? "permission" : null}
                                    onClick={() => {
                                      setSelectedManagedDomainId(domain.id);
                                      setPopup("delete-managed-domain");
                                    }}
                                    t={t}
                                    title={t("admin.detail.action.delete")}
                                  >
                                    <FaTrash aria-hidden="true" />
                                  </AdminActionButton>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {managedDomains.length === 0 ? <p className="admin-detail-empty">{t("admin.organization.domains.managed.empty")}</p> : null}
                  </div>
                  <div className="admin-detail-group">
                    <div className="admin-profile-section-header">
                      <h4 className="admin-detail-group-title">{t("admin.organization.domains.section.connected")}</h4>
                      <div className="admin-organization-users-actions admin-profile-section-actions">
                        <AdminActionButton
                          ariaLabel={t("admin.organization.domains.connected.add")}
                          className="admin-organization-users-icon-action"
                          disabled={isBusy || connectedDomainsCreateDisabled}
                          disabledReason={!canManageDomains ? "permission" : null}
                          onClick={handleAddConnectedDomain}
                          t={t}
                          title={t("admin.organization.domains.connected.add")}
                        >
                          <MdAdd aria-hidden="true" />
                        </AdminActionButton>
                      </div>
                    </div>
                    <div className="admin-profile-terms-table-wrap admin-organization-users-table-wrap">
                      <table className="admin-profile-terms-table admin-organization-users-table">
                        <thead>
                          <tr>
                            <th scope="col">{t("admin.organization.domains.connected.hostname")}</th>
                            <th scope="col">{t("admin.organization.domains.connected.target")}</th>
                            <th scope="col">{t("admin.organization.domains.connected.verified")}</th>
                            <th scope="col">{t("admin.organization.domains.status")}</th>
                            <th scope="col">{t("admin.profile.terms.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {connectedDomains.map((domain) => {
                            const managedTarget = managedDomains.find((managedDomain) => managedDomain.id === domain.managedDomainId);
                            return (
                              <tr key={domain.id}>
                                <td data-label={t("admin.organization.domains.connected.hostname")}>{domain.hostname}</td>
                                <td data-label={t("admin.organization.domains.connected.target")}>
                                  {managedTarget ? toManagedHostname(managedTarget.subdomainLabel) : t("admin.organization.domains.connected.targetMissing")}
                                </td>
                                <td data-label={t("admin.organization.domains.connected.verified")}>
                                  {domain.verifiedAt ? formatFullDateForUiLanguage(domain.verifiedAt, language) : t("admin.organization.domains.connected.notVerified")}
                                </td>
                                <td data-label={t("admin.organization.domains.status")}>
                                  {domain.archivedAt ? t("admin.organization.domains.status.archived") : t("admin.organization.domains.status.active")}
                                </td>
                                <td data-label={t("admin.profile.terms.actions")}>
                                  <div className="admin-organization-users-actions">
                                    <AdminActionButton
                                      ariaLabel={t("admin.organization.domains.connected.verify")}
                                      className="admin-organization-users-icon-action"
                                      disabled={isBusy || !canManageDomains || Boolean(domain.archivedAt)}
                                      disabledReason={!canManageDomains ? "permission" : null}
                                      onClick={() => handleOpenVerifyConnectedDomain(domain.id)}
                                      t={t}
                                      title={t("admin.organization.domains.connected.verify")}
                                    >
                                      <PiSealCheck aria-hidden="true" />
                                    </AdminActionButton>
                                    <AdminActionButton
                                      ariaLabel={domain.archivedAt ? t("admin.detail.action.unarchive") : t("admin.detail.action.archive")}
                                      className="admin-organization-users-icon-action"
                                      disabled={isBusy || !canManageDomains}
                                      disabledReason={!canManageDomains ? "permission" : null}
                                      onClick={() => {
                                        setSelectedConnectedDomainId(domain.id);
                                        setPopup(domain.archivedAt ? "unarchive-connected-domain" : "archive-connected-domain");
                                      }}
                                      t={t}
                                      title={domain.archivedAt ? t("admin.detail.action.unarchive") : t("admin.detail.action.archive")}
                                    >
                                      {domain.archivedAt ? <MdUnarchive aria-hidden="true" /> : <MdArchive aria-hidden="true" />}
                                    </AdminActionButton>
                                    <AdminActionButton
                                      ariaLabel={t("admin.detail.action.delete")}
                                      className="admin-organization-users-icon-action danger"
                                      disabled={isBusy || !canManageDomains || !Boolean(domain.archivedAt)}
                                      disabledReason={!canManageDomains ? "permission" : null}
                                      onClick={() => {
                                        setSelectedConnectedDomainId(domain.id);
                                        setPopup("delete-connected-domain");
                                      }}
                                      t={t}
                                      title={t("admin.detail.action.delete")}
                                    >
                                      <FaTrash aria-hidden="true" />
                                    </AdminActionButton>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {connectedDomains.length === 0 ? <p className="admin-detail-empty">{t("admin.organization.domains.connected.empty")}</p> : null}
                  </div>
                </>
              ) : (
                <p className="admin-detail-empty">{t("admin.organization.domains.empty")}</p>
              )}
            </div>

            <div
              aria-labelledby="admin-organization-internals-tab"
              className={`admin-profile-panel${activeTab === "internals" ? " is-active" : ""}`}
              id="admin-organization-internals-panel"
              role="tabpanel"
            >
              <div className="admin-profile-section-header">
                <div className="admin-detail-actions admin-profile-section-actions">
                  <AdminDetailActions
                    canCreate={false}
                    canUpdate={currentUserSuperAdmin}
                    hasDetail={internalsDetail !== null}
                    isArchived={false}
                    isBusy={isBusy || isInternalsLoading}
                    isDesktopNavHidden={isDesktopNavHidden}
                    isEditing={mode === "edit" && activeTab === "internals"}
                    isMobileViewport={isMobileViewport}
                    onArchive={() => {}}
                    onCloseDesktopNav={onCloseDesktopNav}
                    onCreate={handleCreate}
                    onDiscard={() => setPopup("discard")}
                    onEdit={handleEditInternals}
                    onOpenDesktopNav={onOpenDesktopNav}
                    onSave={() => void handleSave()}
                    onUnarchive={() => {}}
                    showArchiveActions={false}
                    showCreateAction={false}
                    showNavToggle={false}
                    updateDisabledReason={currentUserSuperAdmin ? null : "permission"}
                    t={t}
                  />
                </div>
              </div>
              {internalsErrorMessage ? (
                <div className="admin-detail-state is-error">{internalsErrorMessage}</div>
              ) : null}
              {isInternalsLoading ? (
                <p className="admin-detail-empty">{t("admin.detail.loading")}</p>
              ) : internalsDetail ? (
                <form className="admin-detail-form">
                  <AdminTextField
                    isReadOnly={isReadOnly}
                    label={t("admin.organization.internals.crmId")}
                    onChange={isReadOnly ? undefined : (value) => setInternalsDraft({ crmId: value })}
                    t={t}
                    value={internalsDraft.crmId}
                    visibility="internal"
                  />
                </form>
              ) : (
                <p className="admin-detail-empty">{t("admin.detail.empty")}</p>
              )}
            </div>

            <div
              aria-labelledby="admin-organization-subscription-tab"
              className={`admin-profile-panel${activeTab === "subscription" ? " is-active" : ""}`}
              id="admin-organization-subscription-panel"
              role="tabpanel"
            >
              <div className="admin-profile-section-header">
                <div className="admin-detail-actions admin-profile-section-actions">
                  <AdminDetailActions
                    canCreate={false}
                    canUpdate={canUpdateSubscription}
                    hasDetail={subscriptionDetail !== null}
                    isArchived={false}
                    isBusy={isBusy || isSubscriptionLoading}
                    isDesktopNavHidden={isDesktopNavHidden}
                    isEditing={isSubscriptionEditing}
                    isMobileViewport={isMobileViewport}
                    onArchive={() => {}}
                    onCloseDesktopNav={onCloseDesktopNav}
                    onCreate={handleCreate}
                    onDiscard={() => setPopup("discard")}
                    onEdit={handleEditSubscription}
                    onOpenDesktopNav={onOpenDesktopNav}
                    onSave={() => void handleSaveSubscription()}
                    onUnarchive={() => {}}
                    showArchiveActions={false}
                    showCreateAction={false}
                    showNavToggle={false}
                    updateDisabledReason={updateSubscriptionDisabledReason}
                    t={t}
                  />
                </div>
              </div>
              {subscriptionErrorMessage ? (
                <div className="admin-detail-state is-error">{subscriptionErrorMessage}</div>
              ) : null}
              {isSubscriptionLoading ? (
                <p className="admin-detail-empty">{t("admin.detail.loading")}</p>
              ) : subscriptionDetail ? (
                <form className="admin-detail-form">
                  <div className="admin-detail-group">
                    <h4 className="admin-detail-group-title">{t("admin.organization.subscription.group.organization")}</h4>
                    <AdminTextField
                      inputClassName={!isSubscriptionEditing ? `admin-detail-status-input ${isSubscriptionExpired ? "is-exceeded" : "is-within"}` : undefined}
                      isReadOnly
                      label={t("admin.detail.field.expiresAt")}
                      trailingAction={isSubscriptionEditing ? (
                        <button
                          aria-label={t("admin.detail.field.expiresAt")}
                          className="admin-detail-input-action"
                          disabled={isBusy}
                          onClick={() => {
                            setSubscriptionPickerValue(toDateTimeLocalInputValue(subscriptionDraft.expiresAt));
                            setPopup("date-picker");
                          }}
                          title={t("admin.detail.field.expiresAt")}
                          type="button"
                        >
                          <MdCalendarMonth aria-hidden="true" />
                        </button>
                      ) : undefined}
                      t={t}
                      value={formatSubscriptionExpiration(subscriptionDraft.expiresAt ?? subscriptionDetail.expiresAt, language, t)}
                      visibility="not-applicable"
                    />
                    <AdminQuotaLimitField
                      currentValue={String(organizationContext?.organizationUsage.activeUserCount ?? "")}
                      currentValueStatus={!isSubscriptionEditing && organizationContext
                        ? organizationContext.organizationUsage.activeUserCount <= organizationContext.activeSubscription.maxActiveUsers ? "within" : "exceeded"
                        : undefined}
                      isLimitReadOnly={isSubscriptionReadOnly}
                      label={t("admin.detail.field.maxActiveUsers")}
                      limitValue={subscriptionDraft.maxActiveUsers}
                      onLimitChange={isSubscriptionReadOnly ? undefined : (value) => setSubscriptionDraft((current) => ({ ...current, maxActiveUsers: value }))}
                      t={t}
                      visibility="not-applicable"
                    />
                  </div>
                  <div className="admin-detail-group">
                    <h4 className="admin-detail-group-title">{t("admin.organization.subscription.group.sites")}</h4>
                    <AdminQuotaLimitField
                      currentValue={String(organizationContext?.organizationUsage.activeSiteCount ?? "")}
                      currentValueStatus={!isSubscriptionEditing && organizationContext
                        ? organizationContext.organizationUsage.activeSiteCount <= organizationContext.activeSubscription.maxActiveSites ? "within" : "exceeded"
                        : undefined}
                      isLimitReadOnly={isSubscriptionReadOnly}
                      label={t("admin.detail.field.maxActiveSites")}
                      limitValue={subscriptionDraft.maxActiveSites}
                      onLimitChange={isSubscriptionReadOnly ? undefined : (value) => setSubscriptionDraft((current) => ({ ...current, maxActiveSites: value }))}
                      t={t}
                      visibility="not-applicable"
                    />
                  </div>
                  <div className="admin-detail-group">
                    <h4 className="admin-detail-group-title">{t("admin.organization.subscription.group.signs")}</h4>
                    <AdminQuotaLimitField
                      currentValue={String(organizationContext?.organizationUsage.activeSignCount ?? "")}
                      currentValueStatus={!isSubscriptionEditing && organizationContext
                        ? organizationContext.organizationUsage.activeSignCount <= organizationContext.activeSubscription.maxActiveSigns ? "within" : "exceeded"
                        : undefined}
                      isLimitReadOnly={isSubscriptionReadOnly}
                      label={t("admin.detail.field.maxActiveSigns")}
                      limitValue={subscriptionDraft.maxActiveSigns}
                      onLimitChange={isSubscriptionReadOnly ? undefined : (value) => setSubscriptionDraft((current) => ({ ...current, maxActiveSigns: value }))}
                      t={t}
                      visibility="not-applicable"
                    />
                  </div>
                  <div className="admin-detail-group">
                    <h4 className="admin-detail-group-title">{t("admin.organization.subscription.group.translations")}</h4>
                    <AdminQuotaLimitField
                      currentValue={String(organizationContext?.organizationUsage.activeTranslationCount ?? "")}
                      currentValueStatus={!isSubscriptionEditing && organizationContext
                        ? organizationContext.organizationUsage.activeTranslationCount <= organizationContext.activeSubscription.maxActiveTranslations ? "within" : "exceeded"
                        : undefined}
                      isLimitReadOnly={isSubscriptionReadOnly}
                      label={t("admin.detail.field.maxActiveTranslations")}
                      limitValue={subscriptionDraft.maxActiveTranslations}
                      onLimitChange={isSubscriptionReadOnly ? undefined : (value) => setSubscriptionDraft((current) => ({ ...current, maxActiveTranslations: value }))}
                      t={t}
                      visibility="not-applicable"
                    />
                    <AdminTextField
                      isReadOnly={isSubscriptionReadOnly}
                      label={t("admin.detail.field.maxTranslationLength")}
                      onChange={isSubscriptionReadOnly ? undefined : (value) => setSubscriptionDraft((current) => ({ ...current, maxTranslationLength: value }))}
                      t={t}
                      value={subscriptionDraft.maxTranslationLength}
                      visibility="not-applicable"
                    />
                  </div>
                </form>
              ) : (
                <p className="admin-detail-empty">{t("admin.organization.subscription.empty")}</p>
              )}
            </div>
          </>
        ) : null}
      </AdminDetailSectionLayout>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={closeConnectedDomainVerificationPopup}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy || !connectedDomainVerificationCanSave}
              onClick={() => void confirmSaveConnectedDomainVerification()}
              type="button"
            >
              {t("admin.organization.domains.connected.saveVerified")}
            </button>
          </>
        )}
        isOpen={popup === "verify-connected-domain"}
        onClose={closeConnectedDomainVerificationPopup}
        title={t("admin.organization.domains.connected.verifyTitle")}
      >
        <div className="admin-detail-form">
          <AdminTextField
            isReadOnly
            label={t("admin.organization.domains.connected.hostname")}
            t={t}
            value={selectedConnectedDomain?.hostname ?? ""}
            visibility="not-applicable"
          />
          <AdminTextField
            isReadOnly
            label={t("admin.organization.domains.connected.recordType")}
            t={t}
            value="CNAME"
            visibility="not-applicable"
          />
          <AdminTextField
            isReadOnly
            label={t("admin.organization.domains.connected.recordTarget")}
            t={t}
            value={selectedVerificationManagedDomain ? toManagedHostname(selectedVerificationManagedDomain.subdomainLabel) : ""}
            visibility="not-applicable"
          />
          <p className="admin-detail-state">{t("admin.organization.domains.connected.setupHint")}</p>
          <div className="admin-organization-users-actions">
            <button
              className="admin-popup-action primary"
              disabled={isBusy || !selectedConnectedDomain || !selectedVerificationManagedDomain}
              onClick={() => void handleVerifyConnectedDomain()}
              type="button"
            >
              {t("admin.organization.domains.connected.verify")}
            </button>
          </div>
          {connectedDomainVerificationResult ? (
            <p className={`admin-detail-state${connectedDomainVerificationResult.verified ? "" : " is-error"}`}>
              {connectedDomainVerificationResult.verified
                ? t("admin.organization.domains.connected.verifySucceeded")
                : resolveAdminDomainErrorMessage(
                  connectedDomainVerificationResult.errorCode,
                  t,
                  t("admin.organization.domains.connected.notVerified"),
                )}
              {!connectedDomainVerificationResult.verified && connectedDomainVerificationResult.actualTargetHostname
                ? ` (${t("admin.organization.domains.connected.actualTarget")}: ${connectedDomainVerificationResult.actualTargetHostname})`
                : ""}
            </p>
          ) : null}
          {connectedDomainVerificationErrorMessage ? (
            <p className="admin-detail-state is-error">{connectedDomainVerificationErrorMessage}</p>
          ) : null}
          <label className="admin-detail-checkbox-field">
            <span className="admin-detail-label-text">{t("admin.organization.domains.connected.overrideLabel")}</span>
            <span className="admin-detail-checkbox-row">
              <input
                checked={connectedDomainVerificationOverride}
                disabled={isBusy}
                onChange={(event) => setConnectedDomainVerificationOverride(event.target.checked)}
                type="checkbox"
              />
              <span>{t("admin.organization.domains.connected.overrideVerified")}</span>
            </span>
          </label>
        </div>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void handlePopupConfirm()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={
          popup === "archive"
          || popup === "discard"
          || popup === "unarchive"
          || popup === "archive-managed-domain"
          || popup === "unarchive-managed-domain"
          || popup === "delete-managed-domain"
          || popup === "archive-connected-domain"
          || popup === "unarchive-connected-domain"
          || popup === "delete-connected-domain"
          || popup === "enable-trial-domain"
          || popup === "disable-trial-domain"
        }
        onClose={() => {
          setPopup(null);
          setPendingTab(null);
          setSelectedManagedDomainId(null);
          setSelectedConnectedDomainId(null);
        }}
        title={popupTitle}
      >
        <p>{popupBody}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => {
                setSubscriptionDraft((current) => ({
                  ...current,
                  expiresAt: fromDateTimeLocalInputValue(subscriptionPickerValue),
                }));
                setPopup(null);
              }}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "date-picker"}
        onClose={() => setPopup(null)}
        title={t("admin.detail.field.expiresAt")}
      >
        <input
          className="admin-detail-input admin-date-picker-input"
          onChange={(event) => setSubscriptionPickerValue(event.target.value)}
          type="datetime-local"
          value={subscriptionPickerValue}
        />
        <div className="admin-date-picker-presets">
          <button
            className="admin-popup-action"
            onClick={() => setSubscriptionPickerValue(toDateTimeLocalInputValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()))}
            type="button"
          >
            {t("admin.organization.subscription.preset.twoWeeks")}
          </button>
          <button
            className="admin-popup-action"
            onClick={() => setSubscriptionPickerValue(toDateTimeLocalInputValue(addMonthsToNow(1).toISOString()))}
            type="button"
          >
            {t("admin.organization.subscription.preset.oneMonth")}
          </button>
          <button
            className="admin-popup-action"
            onClick={() => setSubscriptionPickerValue(toDateTimeLocalInputValue(addYearsToNow(1).toISOString()))}
            type="button"
          >
            {t("admin.organization.subscription.preset.oneYear")}
          </button>
        </div>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy || addUserDisabledReason !== null}
              onClick={() => void confirmAddUser()}
              type="button"
            >
              {t("admin.organizationUsers.invite")}
            </button>
          </>
        )}
        isOpen={popup === "add-user"}
        onClose={() => setPopup(null)}
        title={t("admin.organizationUsers.add")}
      >
        <form className="admin-detail-form">
          <label className="admin-detail-field">
            <span className="admin-detail-label-text">{t("admin.detail.field.email")}</span>
            <input
              className="admin-detail-input"
              onChange={(event) => setInviteDraft((current) => ({ ...current, email: event.target.value }))}
              type="email"
              value={inviteDraft.email}
            />
          </label>
          <label className="admin-detail-field">
            <span className="admin-detail-label-text">{t("admin.detail.field.role")}</span>
            <select
              className="admin-detail-input"
              onChange={(event) => setInviteDraft((current) => ({ ...current, role: event.target.value as AdminOrganizationUserRole }))}
              value={inviteDraft.role}
            >
              {roleOptions.map((role) => (
                <option
                  key={role.value}
                  value={role.value}
                >
                  {role.label}
                </option>
              ))}
            </select>
          </label>
        </form>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void confirmChangeRole()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "change-role"}
        onClose={() => setPopup(null)}
        title={t("admin.organizationUsers.changeRole")}
      >
        <p>{selectedUserNameEmailLabel}</p>
        <label className="admin-detail-field">
          <span className="admin-detail-label-text">{t("admin.detail.field.role")}</span>
          <select
            className="admin-detail-input"
            onChange={(event) => setUserRoleDraft(event.target.value as AdminOrganizationUserRole)}
            value={userRoleDraft}
          >
            {roleOptions.map((role) => (
              <option
                key={role.value}
                value={role.value}
              >
                {role.label}
              </option>
            ))}
          </select>
        </label>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void confirmRemoveUser()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "remove-user"}
        onClose={() => setPopup(null)}
        title={t("admin.organizationUsers.remove")}
      >
        <p>{t("admin.organizationUsers.removeConfirm")} {selectedUserLabel}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void confirmCancelInvitation()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "cancel-invitation"}
        onClose={() => setPopup(null)}
        title={t("admin.organizationUsers.cancelInvitation")}
      >
        <p>{t("admin.organizationUsers.cancelInvitationConfirm")} {selectedUserLabel}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void confirmResendInvitation()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "resend-invitation"}
        onClose={() => setPopup(null)}
        title={t("admin.organizationUsers.resendInvitation")}
      >
        <p>{t("admin.organizationUsers.resendInvitationConfirm")} {selectedUserLabel}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy || isManagedDomainAvailabilityLoading || managedDomainDraft.subdomainLabel.trim().length === 0 || !managedDomainAvailability?.available}
              onClick={() => void handlePopupConfirm()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "add-managed-domain"}
        onClose={() => setPopup(null)}
        title={t("admin.organization.domains.managed.add")}
      >
        <div className="admin-detail-form">
          <AdminTextField
            isReadOnly={false}
            label={t("admin.organization.domains.managed.label")}
            onChange={(value) => {
              setManagedDomainDraft({ subdomainLabel: value });
              setManagedDomainAvailability(null);
            }}
            t={t}
            value={managedDomainDraft.subdomainLabel}
            visibility="not-applicable"
          />
          {managedDomainAvailabilityState ? (
            <p className={`admin-detail-state${managedDomainAvailabilityState === "unavailable" ? " is-error" : ""}`}>
              {managedDomainAvailabilityState === "checking"
                ? t("admin.organization.domains.managed.availability.checking")
                : managedDomainAvailabilityState === "available"
                  ? t("admin.organization.domains.managed.availability.available")
                  : managedDomainAvailability?.errorCode
                    ? resolveAdminDomainErrorMessage(managedDomainAvailability.errorCode, t, t("admin.organization.domains.managed.availability.unavailable"))
                    : t("admin.organization.domains.managed.availability.unavailable")}
            </p>
          ) : null}
          <AdminTextField
            isReadOnly
            label={t("admin.organization.domains.managed.hostname")}
            t={t}
            value={toManagedHostname(managedDomainAvailability?.normalizedSubdomainLabel ?? managedDomainDraft.subdomainLabel)}
            visibility="not-applicable"
          />
        </div>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy || connectedDomainDraft.hostname.trim().length === 0 || connectedDomainDraft.managedDomainId.trim().length === 0}
              onClick={() => void handlePopupConfirm()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "add-connected-domain"}
        onClose={() => setPopup(null)}
        title={t("admin.organization.domains.connected.add")}
      >
        <div className="admin-detail-form">
          <AdminTextField
            isReadOnly={false}
            label={t("admin.organization.domains.connected.hostname")}
            onChange={(value) => setConnectedDomainDraft((current) => ({ ...current, hostname: value }))}
            t={t}
            value={connectedDomainDraft.hostname}
            visibility="not-applicable"
          />
          <AdminSelectField
            isReadOnly={false}
            label={t("admin.organization.domains.connected.target")}
            onChange={(value) => setConnectedDomainDraft((current) => ({ ...current, managedDomainId: value }))}
            options={managedDomainOptions}
            t={t}
            value={connectedDomainDraft.managedDomainId}
            visibility="not-applicable"
          />
          <AdminTextField
            isReadOnly
            label={t("admin.organization.domains.connected.recordType")}
            t={t}
            value="CNAME"
            visibility="not-applicable"
          />
          <AdminTextField
            isReadOnly
            label={t("admin.organization.domains.connected.recordTarget")}
            t={t}
            value={selectedConnectedManagedDomain ? toManagedHostname(selectedConnectedManagedDomain.subdomainLabel) : ""}
            visibility="not-applicable"
          />
          <p className="admin-detail-state">{t("admin.organization.domains.connected.setupHint")}</p>
        </div>
      </AppPopup>
    </>
  );
};

const resolveAdminDomainErrorMessage = (
  error: unknown,
  t: (key: TranslationKey) => string,
  fallback: string,
) => {
  const errorCode = extractAdminDomainErrorCode(error);
  if (!errorCode) {
    return fallback;
  }
  switch (errorCode) {
    case "ORGANIZATION_NOT_FOUND":
      return t("admin.organization.domains.error.organizationNotFound");
    case "ORGANIZATION_DOMAIN_SETTINGS_NOT_FOUND":
      return t("admin.organization.domains.error.settingsNotFound");
    case "MANAGED_DOMAIN_LABEL_INVALID":
      return t("admin.organization.domains.error.managedLabelInvalid");
    case "MANAGED_DOMAIN_LABEL_RESERVED":
      return t("admin.organization.domains.error.managedLabelReserved");
    case "MANAGED_DOMAIN_LABEL_UNAVAILABLE":
      return t("admin.organization.domains.error.managedLabelUnavailable");
    case "MANAGED_DOMAIN_HOSTNAME_UNAVAILABLE":
      return t("admin.organization.domains.error.managedHostnameUnavailable");
    case "MANAGED_DOMAIN_NOT_FOUND":
      return t("admin.organization.domains.error.managedDomainNotFound");
    case "MANAGED_DOMAIN_ARCHIVED":
      return t("admin.organization.domains.error.managedDomainArchived");
    case "MANAGED_DOMAIN_IN_USE_BY_CONNECTED_DOMAIN":
      return t("admin.organization.domains.error.managedDomainInUse");
    case "DNS_CONFIGURATION_INVALID":
      return t("admin.organization.domains.error.dnsConfigurationInvalid");
    case "DNS_OPERATION_FAILED":
      return t("admin.organization.domains.error.dnsOperationFailed");
    case "CONNECTED_DOMAIN_HOSTNAME_INVALID":
      return t("admin.organization.domains.error.connectedHostnameInvalid");
    case "CONNECTED_DOMAIN_HOSTNAME_UNAVAILABLE":
      return t("admin.organization.domains.error.connectedHostnameUnavailable");
    case "CONNECTED_DOMAIN_APEX_NOT_ALLOWED":
      return t("admin.organization.domains.error.connectedApexNotAllowed");
    case "CONNECTED_DOMAIN_NOT_FOUND":
      return t("admin.organization.domains.error.connectedDomainNotFound");
    case "CONNECTED_DOMAIN_ARCHIVED":
      return t("admin.organization.domains.error.connectedDomainArchived");
    case "CONNECTED_DOMAIN_CNAME_NOT_FOUND":
      return t("admin.organization.domains.error.connectedCnameNotFound");
    case "CONNECTED_DOMAIN_TARGET_MISMATCH":
      return t("admin.organization.domains.error.connectedTargetMismatch");
    default:
      return fallback;
  }
};

const extractAdminDomainErrorCode = (error: unknown) => {
  const requestErrorCode = extractRequestErrorCode(error);
  if (requestErrorCode) {
    return requestErrorCode;
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error.trim();
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim();
  }
  if (error && typeof error === "object" && "errorCode" in error) {
    const errorCode = error.errorCode;
    if (typeof errorCode === "string" && errorCode.trim().length > 0) {
      return errorCode.trim();
    }
  }
  return null;
};

const toSubscriptionDraft = (detail: AdminOrganizationSubscriptionDetail): AdminOrganizationSubscriptionDraft => ({
  expiresAt: detail.expiresAt,
  maxActiveSigns: String(detail.maxActiveSigns),
  maxActiveSites: String(detail.maxActiveSites),
  maxActiveTranslations: String(detail.maxActiveTranslations),
  maxActiveUsers: String(detail.maxActiveUsers),
  maxTranslationLength: String(detail.maxTranslationLength),
});

const parseSubscriptionNumber = (value: string, label: string) => {
  const normalized = value.trim();
  if (normalized.length === 0) {
    throw new Error(`${label} is required.`);
  }
  if (!/^\d+$/.test(normalized)) {
    throw new Error(`${label} must be a whole number.`);
  }
  return Number(normalized);
};

const formatSubscriptionExpiration = (
  value: null | string,
  language: string,
  t: (key: TranslationKey) => string,
) => (value ? formatFullDateForUiLanguage(value, language) : t("admin.organizationUsers.notApplicable"));

const toDateTimeLocalInputValue = (value: null | string) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const fromDateTimeLocalInputValue = (value: string): null | string => {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const addMonthsToNow = (months: number) => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return date;
};

const addYearsToNow = (years: number) => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + years);
  return date;
};

const toManagedHostname = (subdomainLabel: string) => {
  const normalizedLabel = subdomainLabel.trim().toLowerCase();
  const normalizedSuffix = MANAGED_DOMAIN_SUFFIX.trim();
  if (!normalizedLabel || !normalizedSuffix) {
    return normalizedLabel || normalizedSuffix;
  }
  return `${normalizedLabel}.${normalizedSuffix}`;
};
